import type { Socket } from "socket.io-client";

import { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import useSound from "use-sound";

import type { Notification } from "@/store/notification";

import useAuthStore from "@/store/auth";
import { useNotificationStore } from "@/store/notification";

const SocketContext = createContext<Socket | null>(null);

export function useSocket() {
	if (!SocketContext) {
		throw new Error("useSocket must be used within a SocketProvider");
	}
	return useContext(SocketContext);
}

const SOCKET_URL = `${import.meta.env.VITE_BASE_URL?.replace("/v1", "")}/notifications`;
export function SocketProvider({ children }: { children: React.ReactNode }) {
	const tokens = useAuthStore(state => state.tokens);
	const addNotification = useNotificationStore(state => state.addNotification);
	const [socket, setSocket] = useState<Socket | null>(null);

	const [play] = useSound("/audio/notification.mp3");

	const handleNewNotification = (notification: Notification) => {
		addNotification(notification);
		play();
	};

	useEffect(() => {
		const socket = io(SOCKET_URL, {
			forceNew: true,
			reconnection: true,
			extraHeaders: {
				Authorization: `Bearer ${tokens?.accessToken}`
			}
		});

		setSocket(socket);
		socket.on("connect", () => {});
		socket.emit("SUBSCRIBE_TO_NOTIFICATIONS");
		socket.on("NEW_NOTIFICATION", handleNewNotification);

		return () => {
			socket.disconnect();
		};
	}, [tokens?.accessToken]);

	return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
}
